import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deleteHsaReceipt,
  listHsaReceipts,
} from "../../lib/api-modules/hsa-receipts";
import type { HsaReceipt } from "../../lib/api-modules/types";
import { formatCentsCurrency } from "../../lib/util/money";
import { ConfirmButton } from "../confirm-button";
import { ErrorMessage } from "../auth/error-message";
import { HsaReceiptForm } from "./HsaReceiptForm";

/**
 * HSA receipts panel — Phase 5 Session 5. Renders below an HSA account
 * drilldown. Shows three rollup tiles (total unreimbursed, count of
 * receipts, qualified-vs-non-qualified mix) plus a CRUD list and an
 * inline create form.
 *
 * Phase 7 wires the actual 1099-SA / Form 8889 prep against this data.
 * Phase 10 wires Textract OCR for scanned receipt images.
 */
export function HsaReceiptsPanel({
  accountId,
  canWrite,
}: {
  readonly accountId: string;
  readonly canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["hsa-receipts", accountId],
    queryFn: () => listHsaReceipts({ account_id: accountId }),
  });

  const summary = useMemo(() => {
    const receipts = data?.hsa_receipts ?? [];
    let unreimbursedCents = 0;
    let qualifiedCents = 0;
    let totalCents = 0;
    for (const r of receipts) {
      totalCents += r.amount_cents;
      if (r.status === "unreimbursed") unreimbursedCents += r.amount_cents;
      if (r.is_qualified_medical) qualifiedCents += r.amount_cents;
    }
    return {
      receipts,
      count: receipts.length,
      unreimbursedCents,
      totalCents,
      qualifiedPct:
        totalCents > 0 ? Math.round((qualifiedCents / totalCents) * 100) : 0,
    };
  }, [data]);

  const deleteMutation = useMutation({
    mutationFn: async (r: HsaReceipt) => {
      return deleteHsaReceipt(r.service_date, r.receipt_id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["hsa-receipts", accountId],
      });
    },
  });

  return (
    <section
      aria-labelledby="hsa-receipts-heading"
      className="mt-6 rounded-md border border-zinc-200 p-4 dark:border-zinc-800"
    >
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 id="hsa-receipts-heading" className="text-base font-semibold">
          HSA receipt vault
        </h3>
        {canWrite && !adding && !editing && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
          >
            + Add receipt
          </button>
        )}
      </header>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile
          label="Receipts"
          value={summary.count.toString()}
        />
        <Tile
          label="Total banked"
          value={formatCentsCurrency(summary.totalCents)}
        />
        <Tile
          label="Unreimbursed"
          value={formatCentsCurrency(summary.unreimbursedCents)}
          tone="positive"
        />
        <Tile
          label="% Qualified"
          value={`${summary.qualifiedPct}%`}
        />
      </div>

      {isPending && (
        <p className="mt-3 text-sm text-zinc-500">Loading receipts…</p>
      )}
      {isError && (
        <ErrorMessage>
          Couldn't load receipts:{" "}
          {error instanceof Error ? error.message : "unknown"}
        </ErrorMessage>
      )}

      {adding && (
        <div className="mt-4">
          <HsaReceiptForm
            accountId={accountId}
            onClose={() => setAdding(false)}
          />
        </div>
      )}

      {!isPending && summary.receipts.length > 0 && (
        <ul className="mt-4 space-y-2">
          {summary.receipts.map((r) => (
            <li
              key={r.receipt_id}
              className="rounded-md border border-zinc-200 dark:border-zinc-800"
            >
              {editing === r.receipt_id ? (
                <div className="p-3">
                  <HsaReceiptForm
                    accountId={accountId}
                    receipt={r}
                    onClose={() => setEditing(null)}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-[8rem_1fr_auto_auto] sm:items-center">
                  <div className="text-sm tabular-nums">{r.service_date}</div>
                  <div className="text-sm">
                    <strong>{r.provider}</strong>
                    {r.description ? (
                      <span className="ml-1 text-zinc-500">
                        — {r.description}
                      </span>
                    ) : null}
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      <Chip
                        tone={r.status === "reimbursed" ? "muted" : "positive"}
                      >
                        {r.status}
                      </Chip>
                      {!r.is_qualified_medical && (
                        <Chip tone="warning">non-qualified</Chip>
                      )}
                    </div>
                  </div>
                  <div className="text-right text-sm tabular-nums">
                    {formatCentsCurrency(r.amount_cents)}
                  </div>
                  {canWrite && (
                    <div className="flex flex-wrap gap-2 sm:justify-end">
                      <button
                        type="button"
                        onClick={() => setEditing(r.receipt_id)}
                        className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
                      >
                        Edit
                      </button>
                      <ConfirmButton
                        label="Delete"
                        confirmLabel="Confirm delete"
                        onConfirm={() => deleteMutation.mutate(r)}
                        isPending={deleteMutation.isPending}
                        size="xs"
                      />
                    </div>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {!isPending && summary.receipts.length === 0 && !adding && (
        <p className="mt-4 text-sm text-zinc-500">
          No receipts yet. {canWrite ? "Add the first one above." : ""}
        </p>
      )}

      <p className="mt-4 text-xs text-zinc-500">
        Receipts banked here can be reimbursed as tax-free HSA distributions
        in any future year. Phase 7 wires Form 8889 prep + 1099-SA matching;
        Phase 10 will OCR scanned receipt images.
      </p>
    </section>
  );
}

function Tile({
  label,
  value,
  tone = "neutral",
}: {
  readonly label: string;
  readonly value: string;
  readonly tone?: "neutral" | "positive" | "warning" | "muted";
}) {
  const toneClass =
    tone === "positive"
      ? "text-emerald-700 dark:text-emerald-300"
      : tone === "warning"
        ? "text-amber-700 dark:text-amber-300"
        : tone === "muted"
          ? "text-zinc-500"
          : "";
  return (
    <div className="rounded-md border border-zinc-200 p-3 dark:border-zinc-800">
      <p className="text-xs text-zinc-500">{label}</p>
      <p className={`mt-1 text-lg font-medium tabular-nums ${toneClass}`}>
        {value}
      </p>
    </div>
  );
}

function Chip({
  children,
  tone = "neutral",
}: {
  readonly children: React.ReactNode;
  readonly tone?: "neutral" | "positive" | "warning" | "muted";
}) {
  const toneClass =
    tone === "positive"
      ? "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300"
      : tone === "warning"
        ? "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-300"
        : tone === "muted"
          ? "border-zinc-300 bg-zinc-50 text-zinc-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400"
          : "border-zinc-300 bg-white text-zinc-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide ${toneClass}`}
    >
      {children}
    </span>
  );
}
